import { Component, OnInit } from '@angular/core';
import { formatUsd } from '../../shared/formatting/usd-currency';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { HeaderToolbarComponent } from '../../shared/header-toolbar/header-toolbar.component';
import { QuoteService, AccountDetail, AccountPayment } from '../../services/quote.service';
import { AuthService } from '../../services/auth.service';
import { QuoteUtilsService } from '../../services/quote-utils.service';
import { AccountServicingService, RecordPaymentRequest, AccountFee } from '../../services/account-servicing.service';
import { QuoteStatus, PaymentType, Coverage } from '../../models/quote.model';
import { UserInfo } from '../../models/auth.model';
import { EmailQuoteModalComponent, EmailQuoteModalData } from '../../shared/email-quote-modal/email-quote-modal.component';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../shared/confirm-dialog/confirm-dialog.component';
import { PdfPreviewDialogComponent, PdfPreviewDialogData } from '../../shared/pdf-preview-dialog/pdf-preview-dialog.component';
import {
  RecordPaymentDialogComponent,
  RecordPaymentDialogData,
  RecordPaymentDialogResult
} from '../../shared/record-payment-dialog/record-payment-dialog.component';
import { WaiveFeeDialogComponent, WaiveFeeDialogResult } from '../../shared/waive-fee-dialog/waive-fee-dialog.component';

/** A node on the signature flight-path timeline. */
interface PathNode {
  left: number;   // % from left
  bottom: number; // % from bottom (rides the ascending line)
  type: 'takeoff' | 'now' | 'end';
  label: string;
}

@Component({
  selector: 'app-account-detail',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatDialogModule,
    HeaderToolbarComponent
  ],
  templateUrl: './account-detail.component.html',
  styleUrls: ['./account-detail.component.scss']
})
export class AccountDetailComponent implements OnInit {
  account: AccountDetail | null = null;
  loading = true;
  currentUser: UserInfo | null = null;

  // KAN-39 · The /quotes/{id}/account aggregate does not carry the policy
  // line-items, so we fetch them from the underlying quote (same id) to surface
  // a Policies section. Terms are locked once an account is accepted.
  policies: any[] = [];

  // KAN-38 · Servicing-hub action state. In-flight flags disable their button so
  // a producer can't double-fire a link/PDF request.
  copyingLink = false;
  downloadingPdf = false;

  // KAN-41 · In-flight flags for the three wired servicing actions (payoff,
  // record-payment, cancel) — same double-fire guard as above.
  payoffLoading = false;
  recordingPayment = false;
  cancelLoading = false;

  // KAN-5 phase 2 · agent signing state.
  agentSigning = false;         // preparing / redirecting to sign
  agentSignReturning = false;   // back from signing, polling for completion

  // Assessed Late / NSF fees + waive (super-admin only).
  fees: AccountFee[] = [];
  waivingFeeId: number | null = null;

  /** Waive is a super-admin action (CEO / finance admin), per the CFL fee schedule. */
  get isSuperAdmin(): boolean {
    return this.authService.hasSuperAdminRole();
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private quoteService: QuoteService,
    private authService: AuthService,
    private quoteUtils: QuoteUtilsService,
    private servicing: AccountServicingService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authService.getCurrentUser();
    if (!this.currentUser) {
      this.router.navigate(['/login']);
      return;
    }

    const id = this.route.snapshot.params['id'];
    if (id) {
      this.loadAccount(+id);
      // Returning from the agent signing ceremony: confirm completion with the server.
      if (this.route.snapshot.queryParams['returnedFrom'] === 'agent-signing') {
        this.stripAgentReturnParam();
        this.agentSignReturning = true;
        this.pollAgentStatus(+id, 0);
      }
    } else {
      this.router.navigate(['/quotes']);
    }
  }

  // ---------------------------------------------------------------------------
  //  KAN-5 phase 2 · producer signs as agent
  // ---------------------------------------------------------------------------

  /** True when the agreement is waiting for the agent to sign (customer signed, not yet executed). */
  get awaitingAgentSignature(): boolean {
    return !!this.account?.agreementSignedAt && !this.account?.agreementCompletedAt;
  }

  /** Create the embedded signing URL (backend stamps this producer onto recipient 2) and redirect. */
  signAsAgent(): void {
    if (!this.account || this.agentSigning) return;
    this.agentSigning = true;
    this.quoteService.createAgentSigningUrl(this.account.quoteId).subscribe({
      next: (res) => { window.location.href = res.url; },
      error: () => {
        this.agentSigning = false;
        this.snackBar.open('Could not start agent signing. Please try again.', 'Close', {
          duration: 5000, verticalPosition: 'bottom', panelClass: ['error']
        });
      }
    });
  }

  /** One-shot server reconcile: if the agent has actually signed, the backend sets the flag and we reload. */
  private reconcileAgentStatus(id: number): void {
    this.quoteService.getAgentSigningStatus(id).subscribe({
      next: (r) => { if (r.completed) this.loadAccount(id); },
      error: () => { /* leave as-is; the cron/webhook will catch up */ }
    });
  }

  private pollAgentStatus(id: number, attempt: number): void {
    const maxAttempts = 10; // ~20s at 2s intervals
    this.quoteService.getAgentSigningStatus(id).subscribe({
      next: (r) => {
        if (r.completed) {
          this.agentSignReturning = false;
          this.loadAccount(id);
          return;
        }
        if (attempt >= maxAttempts) {
          this.agentSignReturning = false;
          this.snackBar.open("We couldn't confirm the signature yet. Please refresh in a moment.", 'Close', {
            duration: 6000, verticalPosition: 'bottom', panelClass: ['warning']
          });
          return;
        }
        setTimeout(() => this.pollAgentStatus(id, attempt + 1), 2000);
      },
      error: () => {
        if (attempt >= maxAttempts) { this.agentSignReturning = false; return; }
        setTimeout(() => this.pollAgentStatus(id, attempt + 1), 2000);
      }
    });
  }

  private stripAgentReturnParam(): void {
    const queryParams = { ...this.route.snapshot.queryParams };
    delete queryParams['returnedFrom'];
    this.router.navigate([], { relativeTo: this.route, queryParams, replaceUrl: true });
  }

  loadAccount(id: number): void {
    this.loading = true;
    this.quoteService.getAccount(id).subscribe({
      next: (account) => {
        this.account = account;
        this.loading = false;
        // IFL-34 · policies are now embedded in the account aggregate, so we no
        // longer issue a separate GET /quotes/{id} (which also triggered a Stripe sync).
        this.policies = account.policies || [];
        this.loadFees(id);
        // KAN-5 · reconcile agent-signature completion on open (the webhook may be absent),
        // so a signed agreement stops showing the Sign button. Skipped while the return poll runs.
        if (this.awaitingAgentSignature && !this.agentSignReturning) {
          this.reconcileAgentStatus(id);
        }
      },
      error: (error) => {
        console.error('Error loading account:', error);
        this.snackBar.open('Failed to load account. Please try again.', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error']
        });
        this.loading = false;
        this.router.navigate(['/quotes']);
      }
    });
  }

  get hasPolicies(): boolean {
    return this.policies.length > 0;
  }

  /** Human-readable coverage label (enum → text) for the Policies section. */
  getCoverageDisplayName(coverage: number | undefined): string {
    switch (coverage) {
      case Coverage.Aircraft: return 'Aircraft';
      default:                return 'Aircraft';
    }
  }

  goBack(): void {
    this.router.navigate(['/quotes']);
  }

  // ---------------------------------------------------------- servicing actions
  //
  // KAN-38 / KAN-41 · "Manage account" servicing hub. Money terms are locked
  // once an account is accepted, so nothing here re-prices the financing. The
  // link/paperwork helpers (email, copy, download) plus three real servicing
  // actions — Pay off, Record manual payment, Cancel — map to producer-
  // authenticated endpoints. The remaining operations (change payment method,
  // refund, reinstate, waive-or-charge fee, restructure) have no producer
  // endpoint yet and render as disabled "coming soon" buttons.

  private snack(message: string, tone: 'success' | 'error' = 'success', durationMs?: number): void {
    this.snackBar.open(message, 'Close', {
      duration: durationMs ?? (tone === 'error' ? 5000 : 3000),
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: [tone]
    });
  }

  // ------------------------------------------------------------------ fees (KAN-6)
  /** Load assessed fees for the account (non-blocking — empty on failure). */
  loadFees(id: number): void {
    this.servicing.listFees(id).subscribe({
      next: (fees) => { this.fees = fees || []; },
      error: () => { this.fees = []; }
    });
  }

  get hasFees(): boolean {
    return this.fees.length > 0;
  }

  /** Total of un-waived fees still owed on the account. Paid Late Fees remain
   * visible for audit/history but are not outstanding. */
  get outstandingFees(): number {
    return this.fees
      .filter(f => !f.isWaived && !this.isFeePaid(f))
      .reduce((sum, f) => sum + f.amount, 0);
  }

  isFeePaid(fee: AccountFee): boolean {
    return fee.isPaid;
  }

  /**
   * Waive a fee — super-admin only, reason required, audit-logged server-side.
   * The dialog requires an audit reason and prevents a second in-flight request.
   */
  waiveFee(fee: AccountFee): void {
    if (!this.account || fee.isWaived || this.isFeePaid(fee) || this.waivingFeeId !== null) {
      return;
    }
    this.dialog.open(WaiveFeeDialogComponent, { width: '440px', data: { type: fee.type, amount: fee.amount } })
      .afterClosed().subscribe((result?: WaiveFeeDialogResult) => {
        if (!result || !this.account) { return; }
        this.waivingFeeId = fee.id;
        this.servicing.waiveFee(this.account.quoteId, fee.id, result.reason).subscribe({
      next: () => {
        this.waivingFeeId = null;
        this.snack(`${fee.type} fee waived.`);
        this.loadFees(this.account!.quoteId);
        this.loadAccount(this.account!.quoteId);   // refresh balance
      },
      error: (err) => {
        this.waivingFeeId = null;
        this.snack(err?.status === 403 ? 'Only a super-admin can waive fees.' : 'Could not waive the fee.', 'error');
      }
        });
      });
  }

  /**
   * Best-effort extraction of a human-readable message from an ApiService
   * error (which wraps the raw server body in `.details`), falling back to the
   * mapped friendly message and finally the supplied default.
   */
  private apiMessage(err: any, fallback: string): string {
    const detail =
      err?.details?.message ||
      err?.details?.error ||
      err?.error?.message;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
    if (typeof err?.details === 'string' && err.details.trim()) {
      return err.details;
    }
    if (typeof err?.message === 'string' && err.message.trim()) {
      return err.message;
    }
    return fallback;
  }

  /**
   * Email the secure account link to the customer (POST /quotes/{id}/send-
   * customer-link, producer-auth). This is how a customer self-serves the
   * things a producer can't do server-side — e.g. updating their payment
   * method — from the customer portal.
   */
  emailAccountLink(): void {
    if (!this.account) {
      return;
    }
    const data: EmailQuoteModalData = {
      quoteId: this.account.quoteId,
      customerName: this.account.insuredName,
      customerEmail: this.account.email
    };
    this.dialog.open(EmailQuoteModalComponent, {
      width: '500px',
      data,
      disableClose: false
    });
  }

  /**
   * Copy the customer's secure account link to the clipboard
   * (GET /quotes/{id}/customer-link, producer-auth).
   */
  copyAccountLink(): void {
    if (!this.account || this.copyingLink) {
      return;
    }
    this.copyingLink = true;
    this.quoteService.getCustomerLink(this.account.quoteId).subscribe({
      next: (res) => {
        const link = res?.customerLink || '';
        const finish = () => {
          this.copyingLink = false;
          this.snack('Account link copied to clipboard.');
        };
        if (link && navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(link).then(finish, () => {
            this.copyingLink = false;
            this.snack('Copy failed. Link: ' + link, 'error');
          });
        } else {
          this.copyingLink = false;
          this.snack(link ? ('Account link: ' + link) : 'No account link is available.', link ? 'success' : 'error');
        }
      },
      error: () => {
        this.copyingLink = false;
        this.snack('Could not fetch the account link. Please try again.', 'error');
      }
    });
  }

  /**
   * View the Premium Finance Agreement PDF (GET /quotes/{id}/pdf, producer-auth)
   * in a preview popup with its own Download button.
   */
  downloadAgreement(): void {
    if (!this.account || this.downloadingPdf) {
      return;
    }
    const quoteId = this.account.quoteId;
    this.downloadingPdf = true;
    this.quoteService.downloadQuotePdf(quoteId).subscribe({
      next: (blob) => {
        this.downloadingPdf = false;
        this.dialog.open(PdfPreviewDialogComponent, {
          data: {
            blob,
            fileName: 'premium-finance-agreement.pdf',
            title: 'Premium Finance Agreement'
          } as PdfPreviewDialogData,
          panelClass: 'pdf-preview-panel',
          maxWidth: '95vw',
          autoFocus: false
        });
      },
      error: () => {
        this.downloadingPdf = false;
        this.snack('Could not open the finance agreement PDF.', 'error');
      }
    });
  }

  // ----------------------------------------------------- wired servicing (KAN-41)

  /**
   * Pay off account. Fetches the live payoff quote (GET /accounts/{id}/payoff-
   * quote), shows a confirm dialog with the amount, and on confirm settles the
   * balance (POST /accounts/{id}/payoff) → snackbar → reload.
   */
  payOffAccount(): void {
    if (!this.account || this.payoffLoading) {
      return;
    }
    const id = this.account.quoteId;
    this.payoffLoading = true;
    this.servicing.getPayoffQuote(id).subscribe({
      next: (quote) => {
        this.payoffLoading = false;
        const data: ConfirmDialogData = {
          title: 'Pay off account',
          message:
            `Settle this account in full for ${this.formatCurrency(quote.payoffAmount)} ` +
            `(as of ${this.formatDate(quote.asOf)})? This records a payoff payment and ` +
            `clears the remaining balance of ${this.formatCurrency(quote.remainingBalance)}. ` +
            `Financing terms are not changed.`,
          confirmText: 'Pay off account',
          cancelText: 'Cancel',
          confirmColor: 'primary'
        };
        this.dialog.open(ConfirmDialogComponent, { width: '480px', data })
          .afterClosed()
          .subscribe((confirmed) => {
            if (!confirmed) {
              return;
            }
            this.payoffLoading = true;
            this.servicing.payoff(id).subscribe({
              next: (res) => {
                this.payoffLoading = false;
                this.snack(res?.message || `Account paid off (${this.formatCurrency(res.payoffAmount)}).`);
                this.loadAccount(id);
              },
              error: (err) => {
                this.payoffLoading = false;
                this.snack(this.apiMessage(err, 'Could not pay off the account. Please try again.'), 'error');
              }
            });
          });
      },
      error: (err) => {
        this.payoffLoading = false;
        this.snack(this.apiMessage(err, 'Could not fetch the payoff quote. Please try again.'), 'error');
      }
    });
  }

  /**
   * Record a manual (off-Stripe) payment. Opens a small form dialog (amount
   * required + optional note); on submit posts it (POST /accounts/{id}/record-
   * payment) → snackbar → reload.
   */
  recordManualPayment(): void {
    if (!this.account || this.recordingPayment) {
      return;
    }
    const id = this.account.quoteId;
    const data: RecordPaymentDialogData = {
      insuredName: this.account.insuredName,
      monthlyPayment: this.account.financing?.monthlyPayment
    };
    this.dialog.open(RecordPaymentDialogComponent, { width: '460px', data })
      .afterClosed()
      .subscribe((result?: RecordPaymentDialogResult) => {
        if (!result) {
          return;
        }
        this.recordingPayment = true;
        const payload: RecordPaymentRequest = { amount: result.amount };
        // Teddy #10 · carry the producer-selected payment type through to the server.
        if (result.paymentType) {
          payload.paymentType = result.paymentType;
        }
        if (result.note) {
          payload.note = result.note;
        }
        this.servicing.recordPayment(id, payload).subscribe({
          next: (res) => {
            this.recordingPayment = false;
            this.snack(`Recorded a ${this.formatCurrency(res.amount)} payment.`);
            this.loadAccount(id);
          },
          error: (err) => {
            this.recordingPayment = false;
            this.snack(this.apiMessage(err, 'Could not record the payment. Please try again.'), 'error');
          }
        });
      });
  }

  /**
   * Cancel account. Confirms intent (copy makes clear this stops billing and
   * marks the account Cancelled), then calls POST /accounts/{id}/cancel — the
   * endpoint returns the estimated unearned refund + CFL note, which we surface
   * in a result snackbar → reload.
   */
  cancelAccount(): void {
    if (!this.account || this.cancelLoading) {
      return;
    }
    const id = this.account.quoteId;
    const data: ConfirmDialogData = {
      title: 'Cancel account',
      message:
        'This stops all future billing and marks the account Cancelled. Any ' +
        'unearned-premium refund is only estimated here and is processed per the ' +
        'applicable CFL cancellation rule — no refund is issued by this action. ' +
        'Continue?',
      confirmText: 'Cancel account',
      cancelText: 'Keep account',
      confirmColor: 'warn'
    };
    this.dialog.open(ConfirmDialogComponent, { width: '500px', data })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.cancelLoading = true;
        this.servicing.cancel(id).subscribe({
          next: (res) => {
            this.cancelLoading = false;
            const headline = res?.cancelled
              ? 'Account cancelled — future billing stopped.'
              : 'Cancellation could not be completed.';
            const estimate = `Estimated unearned refund: ${this.formatCurrency(res?.estimatedUnearnedRefund)}.`;
            const note = res?.note ? ` ${res.note}` : '';
            this.snack(`${headline} ${estimate}${note}`, res?.cancelled ? 'success' : 'error', 9000);
            this.loadAccount(id);
          },
          error: (err) => {
            this.cancelLoading = false;
            this.snack(this.apiMessage(err, 'Could not cancel the account. Please try again.'), 'error');
          }
        });
      });
  }

  // ---------------------------------------------------------------- formatting
  formatCurrency(amount: number | null | undefined): string {
    return this.quoteUtils.formatCurrency(amount || 0);
  }

  /** Currency with cents for accurate payment display. */
  formatWhole(amount: number | null | undefined): string {
    return formatUsd(amount);
  }

  formatDate(date: string | Date | null | undefined): string {
    if (!date) {
      return '—';
    }
    return this.quoteUtils.formatDate(date);
  }

  getStatusDisplayName(status: number): string {
    return this.quoteUtils.getStatusDisplayName(status);
  }

  /** Token-driven badge class for a quote/account status (see .badge--* scss). */
  getStatusBadgeClass(status: number): string {
    switch (status) {
      case QuoteStatus.Quoted:     return 'badge--quoted';
      case QuoteStatus.InProgress: return 'badge--inprogress';
      case QuoteStatus.Complete:   return 'badge--complete';
      case QuoteStatus.Grace:      return 'badge--grace';
      case QuoteStatus.Delinquent: return 'badge--delinquent';
      case QuoteStatus.Cancelled:  return 'badge--cancelled';
      default:                     return 'badge--quoted';
    }
  }

  getPaymentTypeDisplayName(type: number | undefined): string {
    if (type === undefined || type === null) {
      return '—';
    }
    switch (type) {
      case PaymentType.DownPayment:    return 'Down payment';
      case PaymentType.MonthlyPayment: return 'Monthly payment';
      case PaymentType.LateFee:        return 'Late fee';
      case PaymentType.Payoff:         return 'Payoff';
      case PaymentType.Unknown:
      default:                         return 'Unknown';
    }
  }

  /** Token-driven badge class for a Stripe payment status string. */
  getPaymentStatusClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (s === 'succeeded' || s === 'paid') {
      return 'badge--paid';
    }
    if (s === 'failed' || s === 'canceled' || s === 'cancelled') {
      return 'badge--failed';
    }
    return 'badge--pending';
  }

  // ------------------------------------------------------------- account state
  get hasSchedule(): boolean {
    return !!this.account?.schedule?.installments?.length;
  }

  get hasPayments(): boolean {
    return !!this.account?.payments?.length;
  }

  get isDelinquent(): boolean {
    return !!this.account?.delinquentStartedAt;
  }

  get isGrace(): boolean {
    return !this.isDelinquent && !!this.account?.graceStartedAt;
  }

  /** Fraction (0..1) of the total loan repaid so far — drives the flight path. */
  get repaidFraction(): number {
    if (!this.account) {
      return 0;
    }
    const total = this.account.financing?.totalOfPayments
      || ((this.account.financing?.downPayment || 0) + (this.account.financing?.amountFinanced || 0));
    const paid = this.account.amountPaid || 0;
    if (total <= 0) {
      return 0;
    }
    return Math.max(0, Math.min(1, paid / total));
  }

  /**
   * Nodes for the signature flight-path timeline: takeoff (down payment) →
   * current position (repaid-to-date) → destination (paid in full).
   * Coordinates match the SVG line (x 30→570, y 102→29) used across the app.
   */
  getPathNodes(): PathNode[] {
    const t = this.repaidFraction;
    const nodes: PathNode[] = [
      { left: 5, bottom: 20.3, type: 'takeoff', label: 'Down' }
    ];
    // Only surface the "now" marker once it has climbed off the runway and
    // before it reaches the destination, so it never collides with an endpoint.
    if (t > 0.04 && t < 0.96) {
      nodes.push({
        left: 5 + t * 90,
        bottom: 20.3 + t * 57,
        type: 'now',
        label: Math.round(t * 100) + '%'
      });
    }
    nodes.push({ left: 95, bottom: 77.3, type: 'end', label: 'Paid' });
    return nodes;
  }

  /**
   * KAN-A · A terminal account (Cancelled/Complete) is no longer serviceable —
   * the three money-moving servicing actions are disabled in the UI (the server
   * also rejects them via GuardServiceable).
   */
  get isServiceable(): boolean {
    return !(this.account?.status === QuoteStatus.Cancelled || this.account?.status === QuoteStatus.Complete);
  }

  trackByInstallment(_index: number, item: { installment: number }): number {
    return item.installment;
  }

  trackByPayment(index: number, item: AccountPayment): number {
    return item.id ?? index;
  }

  getUserDisplayName(): string {
    return this.authService.getUserDisplayName();
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}
